/**
 * MaxGlassTrack — Gate de version côté client (v1.22.8).
 *
 * Compare la version EMBARQUÉE dans ce bundle (`APP_VERSION`) à la version
 * publiée sur le serveur (`system/appVersion`).
 *
 *  - server.min > APP_VERSION      → blocage DUR (modal non fermable). Le
 *    vieux bundle en cache ne peut plus tourner (ex : écritures directes
 *    d'avant v1.16 désormais interdites par les règles Firestore).
 *  - server.current > APP_VERSION  → bandeau doux « nouvelle version dispo ».
 *
 * Aucun service worker dans ce projet : le cache fautif est celui de la
 * WebView / du navigateur sur `index.html`. `forceReload()` le contourne en
 * ajoutant un cache-bust à l'URL du document (nouvelle clé de cache → HTML
 * re-téléchargé → nouveaux assets hashés chargés).
 *
 * FAIL-OPEN : toute erreur (hors-ligne, doc absent, permission) NE bloque
 * jamais l'app. On ne brique pas l'app sur une vérification de version.
 */
import { doc, onSnapshot } from "firebase/firestore";
import { db, auth, functions } from "../firebase";
import { APP_VERSION } from "../version";

const ADMIN_EMAIL = "newtondesign@gmail.com";

export interface VersionState {
  blocked: boolean;          // min > version courante → blocage dur
  updateAvailable: boolean;  // current > version courante → maj dispo
  serverVersion: string;     // `current` publié (pour l'affichage / le bust)
}

/** Compare "a.b.c" — >0 si x>y, <0 si x<y, 0 si égal. */
export function cmpSemver(x: string, y: string): number {
  const a = String(x).split(".").map((n) => parseInt(n, 10) || 0);
  const b = String(y).split(".").map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < 3; i++) {
    const d = (a[i] || 0) - (b[i] || 0);
    if (d !== 0) return d;
  }
  return 0;
}

const SEMVER = /^\d+\.\d+\.\d+$/;

/**
 * S'abonne en temps réel à `system/appVersion`. Appelle `onChange` à chaque
 * évaluation. Retourne une fonction de désabonnement. Fail-open : sur erreur,
 * on émet un état non bloquant.
 */
export function subscribeAppVersion(onChange: (s: VersionState) => void): () => void {
  const ref = doc(db, "system", "appVersion");
  return onSnapshot(
    ref,
    (snap) => {
      const data = snap.exists() ? snap.data() : {};
      const serverMin = typeof data?.min === "string" && SEMVER.test(data.min) ? data.min : "";
      const serverCur = typeof data?.current === "string" && SEMVER.test(data.current) ? data.current : "";

      const blocked = serverMin !== "" && cmpSemver(serverMin, APP_VERSION) > 0;
      const updateAvailable =
        !blocked && serverCur !== "" && cmpSemver(serverCur, APP_VERSION) > 0;

      onChange({ blocked, updateAvailable, serverVersion: serverCur || serverMin || "" });

      // Le développeur, sur un build FRAIS, publie automatiquement la version
      // courante → tous les autres clients (dont ses vieux caches) voient
      // aussitôt le bandeau. Monotone : jamais de rétrogradation.
      void maybePublishCurrent(serverCur);
    },
    () => {
      // Erreur (permission, réseau) → ne bloque pas.
      onChange({ blocked: false, updateAvailable: false, serverVersion: "" });
    }
  );
}

let publishTried = false;
/** Si l'utilisateur est le dev et que ce build est plus récent que ce qui
 *  est publié, publie `current`. Best-effort, une seule tentative. */
async function maybePublishCurrent(serverCur: string): Promise<void> {
  if (publishTried) return;
  const email = (auth.currentUser?.email || "").toLowerCase();
  if (email !== ADMIN_EMAIL) return;
  if (serverCur !== "" && cmpSemver(APP_VERSION, serverCur) <= 0) return; // déjà à jour ou plus vieux
  publishTried = true;
  try {
    const { httpsCallable } = await import("firebase/functions");
    const call = httpsCallable(functions, "publishAppVersion");
    await call({ current: APP_VERSION });
  } catch {
    // silencieux — best-effort
  }
}

/**
 * Recharge en contournant le cache HTTP (pas de service worker ici).
 * On vide aussi les Cache Storage éventuels, puis on navigue avec un
 * paramètre de cache-bust pour forcer un re-fetch de `index.html`.
 */
export async function forceReload(): Promise<void> {
  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    /* ignore */
  }
  try {
    if (navigator.serviceWorker) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch {
    /* ignore */
  }
  try {
    const url = new URL(window.location.href);
    url.searchParams.set("_v", Date.now().toString());
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
}
